/**
 * Brand mark of the extranet chrome (design: the `Building2` icon in a
 * rounded primary square, before the tenant name) — used by the sidebar
 * header and the login brand panel.
 *
 * The icon is white on the primary square: on the dark brand surface a bare
 * `Icon color="inherit"` would take the page text colour (dark), which the
 * surface theme does not remap. The square follows the tenant brand theme
 * (`--color-primary-*`).
 *
 * Pure chrome — not a registered configurable component.
 */

import { Building2 } from 'lucide-react';

import { Icon } from '@components/base/display';

export interface BrandMarkProps {
  /** `sm` = 32px square / 16px icon (sidebar), `md` = 40px / 20px (login). */
  size?: 'sm' | 'md';
}

const SIZE_CLASSES = {
  sm: { box: 'h-8 w-8', icon: 'sm' },
  md: { box: 'h-10 w-10', icon: 'md' },
} as const;

export function BrandMark({ size = 'sm' }: Readonly<BrandMarkProps>) {
  const { box, icon } = SIZE_CLASSES[size];
  return (
    <span
      className={`flex ${box} shrink-0 items-center justify-center rounded-md bg-primary-600 text-white`}
      data-testid="extranet-brand-mark"
    >
      <Icon icon={Building2} size={icon} color="inherit" />
    </span>
  );
}
